/**
 * NewWorldMap 页面组件
 * 全屏世界地图 + Studio 标签 overlay + 引导线 + 蓝点
 * 基于现有 worldMap 模块，简化为静态标签展示
 *
 * Figma 设计要点：
 * - 地图上只显示蓝色圆点（不显示 studio logo badges）
 * - 标签浮在地图周围，通过细实线连接到蓝点
 * - 标签无背景卡片，平铺文字
 */

import React, {
  useMemo,
  useRef,
  useState,
  useEffect,
} from 'react';
import * as echarts from 'echarts';
import { EChartsType } from 'echarts';
import { Layout } from 'tea-component';
import { connect, useIntl } from 'umi';
import Loading from '@/pages/dashboard/components/Loading';
import { STH_CONFIG } from '../_config/constants';
import { STUDIO_JSON, IStudio, COUNTRY_COORDINATES, NEW_COUNTRY_COORDINATES, COUNTRY_FULL_NAME } from '../worldMap/const/studio';
import { importJSON } from '@intelligence/utils/echartsUtils';
import StudioLabel from './components/StudioLabel';
import LabelLine from './components/LabelLine';
import useMapLabelPositions from './hooks/useMapLabelPositions';
import { buriedPointPage } from '@home/utils/buriedPoint';
import './index.scss';

const { Content } = Layout;

interface INewWorldMapProps {
  gameCode: string;
}

// 地图缓存
const mapCache: { worldMap: any; } = { worldMap: null };

/**
 * NewWorldMap 页面
 * 全屏地图 + 标签直接渲染在地图上
 */
const NewWorldMap: React.FC<INewWorldMapProps> = () => {
  const { formatMessage } = useIntl();
  const [loading, setLoading] = useState(true);
  const [mapLoading, setMapLoading] = useState(true);
  const [chartInstance, setChartInstance] = useState<EChartsType | null>(null);

  // Move the first studio (Miniclip) to the end so Lighthouse renders before it
  const studioData = useMemo(() => {
    const [first, ...rest] = STUDIO_JSON;
    return [...rest, first];
  }, []);
  const newChartRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const isMounted = useRef(true);

  // 页面打点
  useEffect(() => {
    buriedPointPage({
      pageId: '208001',
      pageName: 'STH World Map',
      extInfo: '',
      pagePath: '/sth/worldmap',
      typeId: "dashboard",
    });
  }, []);

  // 按国家分组 studios
  const studiosByCountry = useMemo(() => {
    const grouped: Record<string, { name: string; isSubStudio: boolean; }[]> = {};
    studioData.forEach((studio) => {
      if (!grouped[studio.country]) {
        grouped[studio.country] = [];
      }
      grouped[studio.country].push({ name: studio.name, isSubStudio: false });

      if (studio.sub_studio) {
        const subCountry = studio.sub_studio.country;
        if (!grouped[subCountry]) {
          grouped[subCountry] = [];
        }
        grouped[subCountry].push({ name: studio.sub_studio.name, isSubStudio: true });
      }
    });
    return grouped;
  }, [studioData]);

  // 准备国家 marker 数据 (用于蓝点 scatter)
  const countryMarkerData = useMemo(() => {
    return Object.entries(studiosByCountry)
      .map(([country, studios]) => {
        const coordinates = COUNTRY_COORDINATES[country];
        if (!coordinates) return null;
        return { country, coordinates, studios };
      })
      .filter(Boolean) as { country: string; coordinates: [number, number][]; studios: { name: string; isSubStudio: boolean; }[]; }[];
  }, [studiosByCountry]);

  // 国家着色数据
  const countryData = useMemo(() => {
    return Object.keys(studiosByCountry).map((country) => ({
      name: COUNTRY_FULL_NAME[country] || country,
      value: 1,
    }));
  }, [studiosByCountry]);

  // 加载地图
  useEffect(() => {
    const loadMapData = async () => {
      try {
        if (mapCache.worldMap || echarts.getMap('world')) {
          if (isMounted.current) setMapLoading(false);
          return;
        }
        const mapData = await importJSON(STH_CONFIG.MAP.CDN_URL);
        if (mapData && isMounted.current) {
          mapCache.worldMap = mapData;
          echarts.registerMap('world', mapData as any);
          setMapLoading(false);
        }
      } catch (e) {
        console.error('[NewWorldMap] Failed to load map data:', e);
        if (isMounted.current) setMapLoading(false);
      }
    };

    loadMapData();

    return () => {
      isMounted.current = false;
    };
  }, []);

  // 初始化 ECharts
  useEffect(() => {
    if (!newChartRef.current || mapLoading) return;

    let instance = echarts.getInstanceByDom(newChartRef.current);
    if (!instance) {
      instance = echarts.init(newChartRef.current, undefined, { renderer: 'canvas' });
    }

    // 蓝色圆点 scatter — 每个国家一个蓝点 (coordinates[0])
    const scatterData: any[] = [];
    const addedCountries = new Set<string>();

    countryMarkerData.forEach((marker) => {
      const { country, coordinates } = marker;
      if (!addedCountries.has(country)) {
        addedCountries.add(country);
        scatterData.push({
          value: coordinates[0],
          name: `${country}-dot`,
        });
      }
    });

    const series: any[] = [
      // Map series (国家着色)
      {
        type: 'map',
        map: 'world',
        geoIndex: 0,
        data: countryData,
        roam: false,
        select: { disabled: true },
        emphasis: { disabled: true },
      },
    ];

    const option = {
      tooltip: { show: false },
      geo: {
        type: 'map',
        map: 'world',
        roam: false,
        zoom: STH_CONFIG.MAP.ZOOM_DEFAULT,
        itemStyle: {
          areaColor: '#E2E8F5',
          borderWidth: 0,
          borderColor: '#FFF'
        },
        emphasis: { disabled: true },
        select: { disabled: true },
        regions: Object.keys(studiosByCountry).map((country) => ({
          name: COUNTRY_FULL_NAME[country] || country,
          itemStyle: {
            borderWidth: 1.5,
            borderColor: '#FFF',
          },
        })),
      },
      series,
      visualMap: {
        show: false,
        min: 0,
        max: 1,
        inRange: { color: ['#E2E8F5', '#C0D4FF'] },
      },
    };

    instance.setOption(option, true);
    setChartInstance(instance);
    setLoading(false);

    const handleResize = () => {
      instance?.resize({ animation: { duration: 300 } });
    };
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      instance?.dispose();
    };
  }, [countryMarkerData, mapLoading, countryData]);

  // 标签像素位置
  const labelPositions = useMapLabelPositions({
    chartInstance,
    studios: studioData,
    containerRef,
    chartRef: newChartRef,
  });

  // 构建 studio name → IStudio 快速查找
  const studioByName = useMemo(() => {
    const map = new Map<string, IStudio>();
    studioData.forEach((s) => {
      map.set(s.name, s);
    });
    return map;
  }, [studioData]);

  // badge 位置（蓝点的像素坐标，用于引导线终点）
  // 直接从 labelPositions 的 coord 转换，无需再遍历 countryMarkerData
  const badgePositions = useMemo(() => {
    if (!chartInstance || !newChartRef.current || !containerRef.current) {
      return new Map<string, { x: number; y: number; }>();
    }

    const chartRect = newChartRef.current.getBoundingClientRect();
    const containerRect = containerRef.current.getBoundingClientRect();
    const offsetX = chartRect.left - containerRect.left;
    const offsetY = chartRect.top - containerRect.top;

    const posMap = new Map<string, { x: number; y: number; }>();

    labelPositions.forEach((pos) => {
      const pixel = chartInstance.convertToPixel({ geoIndex: 0 }, pos.coord);
      if (pixel && pixel.length >= 2) {
        posMap.set(pos.studioName, {
          x: offsetX + pixel[0],
          y: offsetY + pixel[1],
        });
      }
    });

    return posMap;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartInstance, labelPositions]);

  // sub_studio 引导线数据：从主国家蓝点 → sub_studio 国家蓝点（虚线）
  const subStudioLines = useMemo(() => {
    if (!chartInstance || !newChartRef.current || !containerRef.current) {
      return [];
    }

    const chartRect = newChartRef.current.getBoundingClientRect();
    const containerRect = containerRef.current.getBoundingClientRect();
    const offsetX = chartRect.left - containerRect.left;
    const offsetY = chartRect.top - containerRect.top;

    const lines: {
      studioName: string;
      /** Main country dot position (line start) */
      mainDotPosition: { x: number; y: number; };
      /** Sub_studio country dot position (line end) */
      subDotPosition: { x: number; y: number; };
      /** Anchor Y for the vertical line start (mainDot.y) */
      anchorY: number;
    }[] = [];

    studioData.forEach((studio) => {
      if (!studio.sub_studio) return;
      const subCountry = studio.sub_studio.country;
      // Only add extra line when sub_studio is in a different country
      if (subCountry === studio.country) return;

      // Main country coordinates
      const mainCoords =
        NEW_COUNTRY_COORDINATES[studio.country] || COUNTRY_COORDINATES[studio.country];
      if (!mainCoords || mainCoords.length === 0) return;

      const mainPixel = chartInstance.convertToPixel({ geoIndex: 0 }, mainCoords[0]);
      if (!mainPixel || mainPixel.length < 2) return;

      // Sub_studio country coordinates
      const subCoords = COUNTRY_COORDINATES[subCountry];
      if (!subCoords || subCoords.length === 0) return;

      const subPixel = chartInstance.convertToPixel({ geoIndex: 0 }, subCoords[0]);
      if (!subPixel || subPixel.length < 2) return;


      lines.push({
        studioName: studio.name,
        mainDotPosition: {
          x: offsetX + mainPixel[0],
          y: offsetY + mainPixel[1],
        },
        subDotPosition: {
          x: offsetX + subPixel[0],
          y: offsetY + subPixel[1],
        },
        anchorY: offsetY + mainPixel[1],
      });
    });

    return lines;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartInstance, labelPositions, studioData]);

  // 去重蓝点位置（同一国家只渲染一个蓝点，包括 sub_studio 国家）
  const uniqueBadgeDots = useMemo(() => {
    const seen = new Set<string>();
    const dots: { key: string; x: number; y: number; }[] = [];

    labelPositions.forEach((pos) => {
      const coordKey = `${pos.coord[0]},${pos.coord[1]}`;
      if (seen.has(coordKey)) return;
      seen.add(coordKey);

      const badgePos = badgePositions.get(pos.studioName);
      if (badgePos) {
        dots.push({ key: coordKey, x: badgePos.x, y: badgePos.y });
      }
    });

    // Add blue dots for sub_studio countries (e.g. Korea for Shiftup)
    subStudioLines.forEach((sub) => {
      const coordKey = `sub-${sub.studioName}`;
      if (seen.has(coordKey)) return;
      seen.add(coordKey);
      dots.push({ key: coordKey, x: sub.subDotPosition.x, y: sub.subDotPosition.y });
    });

    return dots;
  }, [labelPositions, badgePositions, subStudioLines]);

  // 统计数据
  const totalStudios = studioData.length;
  const totalGames = '100+';
  const totalStaff = '6,000+';

  return (
    <Content>
      <Content.Header
        className="dbd-new-world-map-header"
        title={formatMessage({ id: 'studio_tech_hub_world_map' })}
      />
      <Content.Body full className="dbd-new-world-map-body dbd-scroll-body">
        {/* Gradient overlays */}
        <div className="dbd-new-world-map-gradient dbd-new-world-map-gradient--top" />
        <div className="dbd-new-world-map-gradient dbd-new-world-map-gradient--bottom" />
        {loading && (
          <div className="dbd-new-world-map-loading">
            <Loading />
          </div>
        )}
        <div
          className="dbd-new-world-map-container"
          ref={containerRef}
          style={{ visibility: loading ? 'hidden' : 'visible' }}
        >
          {/* ECharts 地图 + 蓝点 */}
          <div ref={newChartRef} className="dbd-new-world-map-chart" />

          {/* 引导线 overlay (渲染在标签下方) */}
          {labelPositions.map((pos) => {
            const badgePos = badgePositions.get(pos.studioName);
            if (!badgePos) return null;
            return (
              <LabelLine
                key={`line-${pos.studioName}`}
                labelPosition={pos}
                badgePosition={badgePos}
              />
            );
          })}

          {/* Sub-studio 引导线 — 从主国家蓝点连接到 sub_studio 国家蓝点 */}
          {subStudioLines.map((sub) => {
            const { mainDotPosition, subDotPosition } = sub;
            // Horizontal line (hline): from mainDot.x to subDot.x at subDot.y level
            const hLineLeft = Math.min(mainDotPosition.x, subDotPosition.x);
            const hLineWidth = Math.abs(subDotPosition.x - mainDotPosition.x);

            // Build a pseudo labelPosition with anchorY from the mainDot
            // so LabelLine draws vertical from mainDot.y → subDot.y
            const subLabelPos = {
              studioName: sub.studioName,
              x: subDotPosition.x,
              y: subDotPosition.y,
              anchorX: subDotPosition.x,
              anchorY: sub.anchorY,
              coord: [0, 0] as [number, number],
            };

            return (
              <React.Fragment key={`line-sub-${sub.studioName}`}>
                {/* Vertical segment: from mainDot.y (anchorY) to subDot.y */}
                <LabelLine
                  labelPosition={subLabelPos}
                  badgePosition={sub.subDotPosition}
                  dashed
                />
                {/* Horizontal segment (hline) at subDot.y connecting mainDot.x ↔ subDot.x */}
                {hLineWidth > 0 && (
                  <div
                    className="dbd-studio-label__hline dbd-label-line dbd-label-line--dashed-h"
                    style={{
                      position: 'absolute',
                      left: `${hLineLeft}px`,
                      top: `${subLabelPos.anchorY}px`,
                      width: `${hLineWidth}px`,
                      zIndex: 3,
                      pointerEvents: 'none',
                    }}
                  />
                )}
              </React.Fragment>
            );
          })}

          {/* 蓝点 (每个国家仅一个，去重) */}
          {uniqueBadgeDots.map((dot) => (
            <div
              key={`dot-${dot.key}`}
              className="dbd-label-line__dot"
              style={{
                left: `${dot.x}px`,
                top: `${dot.y}px`,
              }}
            />
          ))}

          {/* Studio 标签 overlay */}
          {labelPositions.map((pos) => {
            const studio = studioByName.get(pos.studioName);
            if (!studio) return null;
            const badgePos = badgePositions.get(pos.studioName);
            // Calculate horizontal line data for StudioLabel
            // Left labels: line from label left (0) → badgePos.x
            // Right labels: line from badgePos.x → label right edge (content width)
            let studioLineData: { width: number; offsetX: number; } | undefined;
            // Badge is to the right → line from label origin to badge X
            // 根据屏幕宽度调整 left label 的线条宽度
            const screenWidth = window.innerWidth;
            let extraWidth = 0;
            if (badgePos) {
              const extendsRight = badgePos.x >= pos.x;
              if (extendsRight) {
                if (screenWidth > 1400) {
                  extraWidth = (screenWidth - 1400) / 8;
                }
                studioLineData = {
                  width: badgePos.x - pos.x - extraWidth,
                  offsetX: 0,
                };
              } else {
                // Badge is to the left → line from badge X to label right edge
                // Use estimated label width (170px) as content boundary

                const labelRight = pos.x + (pos.studioName === 'Yongxing' ? 280 : 155);
                const width = labelRight - badgePos.x;
                if (screenWidth > 1400 && pos.studioName !== 'Yongxing') {
                  if (width > 300) {
                    extraWidth = (screenWidth - 1400) / 10 * -1;
                  }
                }
                studioLineData = {
                  width: width + extraWidth,
                  offsetX: badgePos.x - pos.x - extraWidth, // negative offset
                };
              }
            }
            return (
              <StudioLabel
                key={pos.studioName}
                studio={studio}
                position={{ x: pos.x + extraWidth, y: pos.y }}
                lineData={studioLineData}
              />
            );
          })}

          {/* Bottom stats bar */}
          <div className="dbd-new-world-map-stats">
            <div className="dbd-new-world-map-stats__item">
              <span className="dbd-new-world-map-stats__number">{totalStudios}</span>
              <span className="dbd-new-world-map-stats__label">Studio</span>
            </div>
            <div className="dbd-new-world-map-stats__item">
              <span className="dbd-new-world-map-stats__number">{totalStaff}</span>
              <span className="dbd-new-world-map-stats__label">Staff</span>
            </div>
            <div className="dbd-new-world-map-stats__item">
              <span className="dbd-new-world-map-stats__number">{totalGames}</span>
              <span className="dbd-new-world-map-stats__label">Games</span>
            </div>
          </div>
        </div>
      </Content.Body>
    </Content>
  );
};

export default connect((state: any) => ({
  gameCode: state.dashboard?.currentGame?.game_code,
}))(NewWorldMap);
